/*
 * The BFF client.
 *
 * Every call goes to this add-in's own origin — the same Go binary that served
 * this bundle — never to the Valoon gateway. The gateway has no CORS for
 * browsers, and the API key that reaches it must not exist in a browser at
 * all.
 */

/* global fetch, Response, RequestInit */

import type { Session, SessionUser } from "./session";
import type { Suggestion } from "./suggestion";

export interface Project {
  id: string;
  name: string;
  key: string;
}

export interface NewTicket {
  projectId: string;
  title: string;
  description?: string;
  dueDate?: string;
}

export interface CreatedTicket {
  id: string;
  number: number;
}

/**
 * A failure the BFF described. `code` is the machine-readable half of the
 * error envelope and is stable; `message` is written for the user and is not.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }

  /** True when the session is gone and the pane must return to the sign-in gate. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

const NETWORK_MESSAGE =
  "Could not reach the Valoon add-in service. Check your connection and try again.";

async function request<T>(path: string, init: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
    });
  } catch {
    // fetch only rejects on transport failures; everything else is a status.
    throw new ApiError(0, "network", NETWORK_MESSAGE);
  }

  const text = await response.text();
  let body: unknown = undefined;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = undefined;
    }
  }

  if (!response.ok) {
    const envelope = body as { error?: string; message?: string } | undefined;
    throw new ApiError(
      response.status,
      envelope?.error ?? "unknown",
      // A response without our envelope means something other than the BFF
      // answered — a proxy error page, most likely.
      envelope?.message ?? `The service returned an unexpected error (${response.status}).`
    );
  }
  return body as T;
}

function authorized(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` };
}

interface SessionResponse {
  token: string;
  expiresAt: string;
  user: SessionUser;
}

/** Exchanges a Valoon API key for a session token. */
export async function createSession(clientId: string, apiKey: string): Promise<Session> {
  const body = await request<SessionResponse>("/api/auth/session", {
    method: "POST",
    body: JSON.stringify({ clientId, apiKey }),
  });
  return { ...body, clientId };
}

export function listProjects(token: string): Promise<Project[]> {
  return request<Project[]>("/api/projects", { method: "GET", headers: authorized(token) });
}

/**
 * Creates a ticket, sending the suggestion it started from alongside it.
 *
 * The server records the difference between the two. That difference is the
 * only evaluation data this project will ever get for free, and it cannot be
 * reconstructed afterwards: the ticket remembers the answer, not the answer we
 * offered. Omitting the suggestion is legitimate — the email may not have been
 * readable — and simply means there is no correction to record.
 */
export function createTicket(
  token: string,
  fields: NewTicket,
  suggestion?: Suggestion
): Promise<CreatedTicket> {
  return request<CreatedTicket>("/api/tickets", {
    method: "POST",
    headers: authorized(token),
    // JSON.stringify drops an undefined property, so an absent suggestion
    // sends {"fields": …} and nothing else.
    body: JSON.stringify({ fields, suggestion }),
  });
}
